import {
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { ResponsiveRow } from "../../../../constants/layout.constants";
import { formatCurrency, isNullUndefined } from "../../../../util/util";
import ModalContainerModule from "../../../modules/modalContainer";
import { useCallback, useEffect, useState } from "react";
import { orgBankAccounts } from "../../../../services/bank.service";
import { getCurrentAssetCashAccounts } from "../../../../services/list.service";
import { ledgerAccForBank } from "../../../../services/list.service";
import { useSelector } from "react-redux";
import {
  handleErrorResponse,
  issueWarnMessage,
} from "../../../../actions/message";
import { getBalanceOnAccount } from "../../../../services/accounts.service";

export default function MakeTransferModal(props) {
  /**
   *
   * REQUIRED PROPS
   *
   * handleSaveClicked
   * handleCloseClicked
   * makeTransfer
   */

  const [transaction, setTransaction] = useState({});
  const [currentAssetCashAccs, setCurrentAssetCashAccs] = useState([]);
  const [organizationBankList, setOrgBankAccountsList] = useState(false);
  const [ledgerAccountsList, setLedgerAccountsList] = useState([]);
  const [submitAble, setSubmitable] = useState(false);
  const [balanceOnAcc, setBalanceOnAcc] = useState(null);
  const user = useSelector((state) => state.auth.user);

  const handleCancelClicked = () => {
    props.handleCloseClicked();
    setTransaction({
      description: null,
      facilityId: user.proxyFacility,
      fromLedgerAccountId: null,
      toBankAccountId: null,
      toLedgerAccountId: null,
      amount: null,
    });
    setBalanceOnAcc(null);
  };

  const checkSubmittable = useCallback(() => {
    if (
      !isNullUndefined(transaction.fromLedgerAccountId) &&
      !isNullUndefined(transaction.toBankAccountId) &&
      !isNullUndefined(transaction.toLedgerAccountId) &&
      !isNullUndefined(transaction.amount) &&
      transaction.amount <= balanceOnAcc &&
      transaction.amount > 0
    ) {
      setSubmitable(true);
    } else {
      setSubmitable(false);
    }
  }, [transaction, balanceOnAcc, setSubmitable]);

  useEffect(() => {
    orgBankAccounts()
      .then((data) => setOrgBankAccountsList(data))
      .catch((error) => {
        handleErrorResponse("error", error);
      });

    getCurrentAssetCashAccounts()
      .then((data) => {
        setCurrentAssetCashAccs(data);
      })
      .catch((error) => {
        handleErrorResponse(error);
      });

    setTransaction({
      description: null,
      facilityId: user.proxyFacility,
      fromLedgerAccountId: null,
      toBankAccountId: null,
      toLedgerAccountId: null,
      amount: null,
    });
  }, [setOrgBankAccountsList, user.proxyFacility]);

  useEffect(() => {
    if (!isNullUndefined(transaction.toBankAccountId)) {
      ledgerAccForBank(transaction.toBankAccountId)
        .then((data) => setLedgerAccountsList(data))
        .catch((error) => {
          handleErrorResponse(error);
        });
    }
  }, [transaction?.toBankAccountId]);

  useEffect(() => {
    if (!isNullUndefined(transaction.fromLedgerAccountId)) {
      getBalanceOnAccount(transaction.fromLedgerAccountId)
        .then((data) => {
          setBalanceOnAcc(data?.balance);
          checkSubmittable();
        })
        .catch((error) => {
          handleErrorResponse(error);
        });
    }
  }, [checkSubmittable, transaction?.fromLedgerAccountId]);

  useEffect(() => {
    checkSubmittable();
  }, [checkSubmittable, transaction]);

  return (
    <ModalContainerModule
      size="sm"
      accept={() => props.handleSaveClicked(transaction)}
      cancel={handleCancelClicked}
      acceptDisabled={!submitAble}
      acceptDisabled2={!submitAble}
      openModal={props.makeTransfer}
      modalTitle="Cash To Bank Transfer Details"
      acceptButtonText="Save"
    >
      {!isNullUndefined(balanceOnAcc) && (
        <Alert severity="info">
          <AlertTitle>Transferable Balance on Account:</AlertTitle>{" "}
          {formatCurrency(balanceOnAcc)}
        </Alert>
      )}
      <ResponsiveRow container sx={{ height: "auto" }}>
        <FormControl
          item
          sx={{
            width: { xs: "100%", lg: "50%" },
            marginBottom: "10px",
          }}
        >
          <InputLabel>From Account *</InputLabel>
          <Select
            sx={{ width: { xs: "100%", md: "90%" } }}
            id={"bankAccountId"}
            label="From Bank Account *"
            value={transaction?.fromLedgerAccountId ?? ""}
            isrequired={true}
            onChange={(event) => {
              setBalanceOnAcc(null);
              setTransaction({
                ...transaction,
                fromLedgerAccountId: event.target.value,
              });
            }}
          >
            {currentAssetCashAccs?.length > 0 ? (
              currentAssetCashAccs.map(function (account, i) {
                return (
                  <MenuItem
                    key={account.value}
                    value={account.value}
                    sx={{ width: "100%" }}
                  >
                    {account.label}
                  </MenuItem>
                );
              }, this)
            ) : (
              <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
            )}
          </Select>
        </FormControl>
        {!isNullUndefined(balanceOnAcc) && (
          <FormControl
            item
            sx={{
              width: { xs: "100%", lg: "50%" },
              marginBottom: "10px",
            }}
          >
            <InputLabel>Amount *</InputLabel>
            <OutlinedInput
              sx={{ width: "90%" }}
              id={"amount"}
              type="number"
              label="Amount *"
              isrequired={true}
              value={transaction?.amount ?? ""}
              error={
                transaction?.amount > balanceOnAcc ||
                isNullUndefined(transaction?.amount) ||
                transaction?.amount <= 0
              }
              helperText={
                transaction?.amount > balanceOnAcc
                  ? "The amount entered cannot be greater than the balance on the account"
                  : ""
              }
              onChange={(event) => {
                const amount = event.target.value;
                if (amount > balanceOnAcc) {
                  issueWarnMessage(
                    "The amount entered cannot be greater than the balance on the account"
                  );
                }
                setTransaction({
                  ...transaction,
                  amount,
                });
              }}
            />
          </FormControl>
        )}
      </ResponsiveRow>
      <ResponsiveRow container sx={{ height: "auto" }}>
        <FormControl
          item
          sx={{
            width: { xs: "100%", lg: "50%" },
            marginBottom: "10px",
          }}
        >
          <InputLabel>To Bank Account *</InputLabel>
          <Select
            sx={{ width: { xs: "100%", md: "90%" } }}
            id={"bankAccountId"}
            label="To Bank Account *"
            value={transaction?.toBankAccountId ?? ""}
            isrequired={true}
            onChange={(event) =>
              setTransaction({
                ...transaction,
                toBankAccountId: event.target.value,
              })
            }
          >
            {organizationBankList?.length > 0 ? (
              organizationBankList.map(function (account, i) {
                return (
                  <MenuItem key={account.value} value={account.value}>
                    {account.label}
                  </MenuItem>
                );
              }, this)
            ) : (
              <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
            )}
          </Select>
        </FormControl>
        {transaction?.toBankAccountId && (
          <FormControl
            item
            sx={{
              width: { xs: "100%", lg: "50%" },
              marginBottom: "10px",
            }}
          >
            <InputLabel>To Ledger Account *</InputLabel>
            <Select
              sx={{ width: { xs: "100%", md: "90%" } }}
              id={"ledgerAccountId"}
              label="To Ledger Account *"
              value={transaction?.toLedgerAccountId ?? ""}
              isrequired={true}
              onChange={(event) =>
                setTransaction({
                  ...transaction,
                  toLedgerAccountId: event.target.value,
                })
              }
            >
              {ledgerAccountsList?.length > 0 ? (
                ledgerAccountsList.map(function (account, i) {
                  return (
                    <MenuItem key={account.value} value={account.value}>
                      {account.label}
                    </MenuItem>
                  );
                }, this)
              ) : (
                <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
              )}
            </Select>
          </FormControl>
        )}
      </ResponsiveRow>
      <ResponsiveRow container sx={{ height: "auto" }}>
        <FormControl
          item
          sx={{
            width: { xs: "100%", lg: "100%" },
            marginBottom: "10px",
          }}
        >
          <InputLabel>Memo *</InputLabel>
          <OutlinedInput
            label="Memo *"
            id="description"
            type="text"
            sx={{
              width: { xs: "100%", md: "90%" },
            }}
            onChange={(event) => {
              setTransaction({
                ...transaction,
                description: event.target.value,
              });
            }}
            value={transaction?.description ?? ""}
            isrequired={true}
            multiline
            rows={4}
          />
        </FormControl>
      </ResponsiveRow>
    </ModalContainerModule>
  );
}
